import { cloneDeep, orderBy } from 'lodash'

import { store } from 'store/reducers'
import { updateSetting } from 'store/actions/settings'

// Default props of every tables
export const defaultTableValues = {
  name: null, // Table name, used to (access/update) default values in the user' settings
  asyncContent: false,
  header: [],
  isLoading: false,
  isSelectable: false,
  alwaysVisible: false,

  // Default values
  dataKey: '_id',
  detailsMap: {},
  archived: false,
  content: [],
  count: 0,
  filter: [],
  limit: 15,
  page: 1,
  selectedResourcesMap: {},
  visibleColumn: {}
}

/**
 * Function to init a table's value
 * @param {string} name table name
 * @param {array} header header of the table
 * @param {function} reloadCb Function to call to reload the displayed element (change page, filter, etc)
 * @param {object} options Options of the table
 */
export const init = async (name, header, reloadCb, options = {}) => {
  const { settings } = store.getState()

  // Get the sort key, column to display and last used filter from the user's settings
  const sortBy = settings[`table-sort-key-${name}`] || options.defaultSortKey
  const visibleColumn = settings[`table-visible-columns-${name}`] || header.reduce((acc, h) => (h.label && !h.hideByDefault ? { ...acc, [h.key]: 1 } : acc), {})
  const filter = settings[`search-content-parsed-${name}`]
  const page = settings[`last-page-viewed-${name}`] || 1

  store.dispatch({
    ...defaultTableValues,
    ...options,
    type: 'INIT_TABLE',
    name,
    asyncContent: reloadCb ? true : false,
    sortBy,
    visibleColumn,
    filter,
    header,
    _reloadCb: reloadCb,
    page,
    initted: true // Prevents a class of errors where a table is half initted after it has been destroyed
  })
}

// Function to call when a component want's to manually refresh the table
export const triggerReload = async name => {
  const {
    tables: { [name]: tableOptions }
  } = store.getState()
  if (typeof tableOptions !== 'undefined' && typeof tableOptions._reloadCb !== 'undefined') {
    setIsLoading(name, true)

    const [content, count] = await tableOptions._reloadCb(tableOptions)

    setCount(name, count)
    setContent(name, content)
    setIsLoading(name, false)
  }
}

export const collapse = (name, _id, detailType, contentToDisplay) => {
  const { tables } = store.getState()
  const { content, dataKey, detailsMap } = tables[name]

  store.dispatch({
    type: 'SET_COLLAPSED_CONTENT',
    name,
    content: content.map(row => (row[dataKey] === _id ? { ...row, active: row.detailType === detailType ? false : true, detailType } : row)),
    detailsMap: { ...detailsMap, [_id]: contentToDisplay }
  })
}

/**
 * Function to destroy an existing table (ex: leaving the page containing the table)
 * @param {string} name table name
 */
export const destroy = name => {
  const {
    tables: { [name]: { page } = {} } = {},
    settings: { [`last-page-viewed-${name}`]: lastViewedPage }
  } = store.getState()

  if (lastViewedPage !== page) store.dispatch({ type: 'SET_SETTING_VALUE', name: `last-page-viewed-${name}`, value: page })
  store.dispatch({ type: 'DESTROY_TABLE', name })
  store.dispatch({ type: 'REFRESH_COUNTER', name })
}

/**
 * Function to set the visible column
 * @param {string} name table name
 * @param {object} visibleColumn object in the form of key:true/false
 */
export const setVisibleColumn = (name, visibleColumn) => {
  store.dispatch(updateSetting(`table-visible-columns-${name}`, visibleColumn))
  store.dispatch({ type: 'SET_VISIBLE_COLUMN', name, visibleColumn })
}

/**
 * Function to set the sort field
 * @param {string} name table name
 * @param {string} sortBy sort database key
 */
export const setSortBy = (name, sortBy, inverseSort) => {
  const { tables, settings } = store.getState()

  const currentValue = settings[`table-sort-key-${name}`] || ''
  const isTheSameField = currentValue ? currentValue.includes(sortBy) : false
  const isDescending = currentValue.includes('-')
  const addsHyphen = isTheSameField && isDescending ? '' : isTheSameField ? '-' : inverseSort ? '-' : ''

  const newSettingValue = `${addsHyphen}${sortBy}`

  store.dispatch(updateSetting(`table-sort-key-${name}`, newSettingValue))
  store.dispatch({
    type: 'SET_SORT_BY',
    name,
    sortBy: newSettingValue
  })
  // Reload the local content (to apply the new sort)
  if (!tables.asyncContent) setContent(name, tables[name].content)
}

/**
 * Function to set the total amount of element that could be displayed
 * @param {string} name table name
 * @param {integer} count
 */
export const setCount = (name, count) => store.dispatch({ type: 'SET_COUNT', name, count })

/**
 * Function to set the table filter
 * @param {string} name table name
 * @param {array} filter
 */
export const setFilter = (name, filter) => store.dispatch({ type: 'SET_FILTER', name, filter })

/**
 *  Function to set the table headers
 * @param {string} name table name
 * @param {array} header
 */
export const setHeader = (name, header) => {
  const { settings } = store.getState()
  const visibleColumn = settings[`table-visible-columns-${name}`] || header.reduce((acc, h) => (h.label ? { ...acc, [h.key]: 1 } : acc), {})

  store.dispatch({ type: 'SET_HEADER', name, header, visibleColumn })
}

export const setIsLoading = (name, isLoading) => store.dispatch({ type: 'SET_LOADING_TABLE', name, isLoading })

/**
 * Function to set the limit of element per page
 * @param {string} name table name
 * @param {integer} limit defaults to 15
 */
export const setLimit = (name, limit) => store.dispatch({ type: 'SET_LIMIT', name, limit })

/**
 * Function to set the content of the table
 * @param {string} name table name
 * @param {array} content
 */
export const setContent = (name, content) => {
  const { tables } = store.getState()
  const { sortBy, asyncContent } = tables[name]

  if (!asyncContent && sortBy) content = orderBy(content, [sortBy?.replace('-', '')], [sortBy?.[0] === '-' ? 'asc' : 'desc'])
  store.dispatch({ type: 'SET_CONTENT', name, content })
}

/**
 * Function to set the new page to display
 * @param {string} name table name
 * @param {integer} page page number
 */
export const setPage = (name, page) => store.dispatch({ type: 'SET_PAGE', name, page })

/**
 * Function to toggle the display of the archived resources
 * @param {string} name table name
 * @param {boolean} archived
 */
export const toggleDisplayArchive = (name, archived) => store.dispatch({ type: 'SET_DISPLAY_ARCHIVED', name, archived })

/**
 * Function to set the selection
 * @param {string} name table name
 * @param {object} selectedResourcesMap selection hash map
 */
export const setSelection = (name, selectedResourcesMap) => store.dispatch({ type: 'SET_RESOURCES_SELECTION', name, selectedResourcesMap })

/**
 * Function to toggle the selection of a specific element
 * @param {string} name table name
 * @param {*} identifier element identifier (id)
 */
export const toggleResourceSelection = (name, identifier) => {
  const { tables } = store.getState()
  const selectedResources = cloneDeep(tables[name].selectedResourcesMap)

  if (selectedResources[identifier]) delete selectedResources[identifier]
  else selectedResources[identifier] = true
  setSelection(name, selectedResources)
}

/**
 * Function to toggle the selection of the users of a specific page
 * @param {string} name table name
 * @param {array} content current page content
 * @param {boolean} removeUser select (false) /remove (true) the users of the page
 */
export const toggleContentSelection = (name, content, removeUser) => {
  const { tables } = store.getState()
  const selectedResources = cloneDeep(tables[name].selectedResourcesMap)

  setSelection(
    name,
    content.reduce((acc, { [tables[name].dataKey]: _id }) => {
      if (removeUser) delete acc[_id]
      else acc[_id] = true
      return acc
    }, selectedResources)
  )
}

/**
 * Function to select all the users (ignoring the limit)
 * @param {string} name table name
 */
export const selectAllResources = async name => {
  const { tables } = store.getState()
  const { archived, filter, _reloadCb, dataKey } = tables[name]

  const [resources] = _reloadCb ? await _reloadCb({ archived, filter }) : [tables[name].content]
  setSelection(
    name,
    resources.reduce((acc, { [dataKey]: _id }) => {
      acc[_id] = true
      return acc
    }, {})
  )
}
